import { z } from 'zod';
// Brand-coach (s207) — the agent that reads accumulated signal each week
// and proposes evolutions to the brand brain. The shapes here are the
// contract between:
//   - the agent's output (BrainDiff[])
//   - the brand_evolution feed-card payload (wraps the diffs)
//   - the diff applier that bumps the brain on founder approval
//
// See product-spec.md §3.1a (brain-evolution loop) + §4.1 (agent entry).
//
// IMPORTANT — brain whitelist. The coach can only propose changes to a
// curated set of "evolvable" brain fields. Everything else needs
// explicit human authoring. Whitelist enforcement happens here (Zod
// rejects bad paths) and again in the applier (defense in depth).
/** Every brain path the coach is allowed to touch. Strings are exact,
 *  no globs — the applier walks each one explicitly. Array-of-objects
 *  paths use a numeric index segment (e.g. icp_segments.0.pains). */
export const brainDiffPathSchema = z.enum([
    'identity.positioning_statement',
    'identity.value_props',
    'identity.differentiators',
    'voice.must_use_words',
    'voice.must_avoid_words',
    // ICP segments are array-of-objects. We expose two evolvable
    // sub-fields per segment, addressed by zero-based index. The agent
    // sees the index → segment-name mapping in its context.
    'customer.icp_segments.0.pains',
    'customer.icp_segments.0.jobs_to_be_done',
    'customer.icp_segments.1.pains',
    'customer.icp_segments.1.jobs_to_be_done',
    'customer.icp_segments.2.pains',
    'customer.icp_segments.2.jobs_to_be_done',
]);
/** Evidence tags. Small enum so the dashboard can render a colored
 *  pill per source. Free-form description goes in the diff's
 *  `rationale`. */
export const brainDiffEvidenceSourceSchema = z.enum([
    'meta_metrics', // pulled from meta_paid_ad_metrics_history (paid)
    'meta_organic', // pulled from latest meta_engagement_digest + published organic slots (phase 6)
    'google_metrics', // pulled from google_paid_ad_metrics_history
    'analytics_events', // ClickHouse magig_analytics.events
    'feed_decisions', // founder accepted/dismissed history
    'brand_brain', // contradiction with a current brain value
]);
const brainDiffEvidenceSchema = z.object({
    source: brainDiffEvidenceSourceSchema,
    /** Mag-voice one-liner: "engaged cohort on meta skewed 65% mobile
     *  the last 4 weeks, your stated icp is desktop-first". */
    note: z.string().min(1).max(280),
});
/** A single proposed change to the brain. Discriminated on `op`:
 *   - `set`    — replace the value at `path` (string-typed fields)
 *   - `append` — push `value` onto the array at `path`
 *   - `remove` — delete `value` from the array at `path`
 * The applier validates that the op matches the field's actual shape
 * (set on a string, append/remove on an array) before writing v_{n+1}. */
export const brainDiffSchema = z.discriminatedUnion('op', [
    z.object({
        op: z.literal('set'),
        path: brainDiffPathSchema,
        oldValue: z.string().max(2000),
        newValue: z.string().min(1).max(2000),
        rationale: z.string().min(1).max(500),
        evidence: z.array(brainDiffEvidenceSchema).min(1).max(5),
    }),
    z.object({
        op: z.literal('append'),
        path: brainDiffPathSchema,
        /** Item to add to the array. Strings only — every evolvable array
         *  on the whitelist is a string[]. */
        value: z.string().min(1).max(500),
        rationale: z.string().min(1).max(500),
        evidence: z.array(brainDiffEvidenceSchema).min(1).max(5),
    }),
    z.object({
        op: z.literal('remove'),
        path: brainDiffPathSchema,
        /** Must match an item currently in the array — applier verifies. */
        value: z.string().min(1).max(500),
        rationale: z.string().min(1).max(500),
        evidence: z.array(brainDiffEvidenceSchema).min(1).max(5),
    }),
]);
/** Output of runBrandCoachAgent. The agent returns no diffs when the
 *  signal is too thin (cold brand, no recent activity) — that's not an
 *  error, just a no-op tick. */
export const brandCoachOutputSchema = z.object({
    /** Empty when there's nothing to propose. */
    diffs: z.array(brainDiffSchema).max(8),
    /** One-paragraph Mag-voice summary of what she noticed this week.
     *  Renders at the top of the feed card. Empty string when diffs is
     *  empty (the card isn't created in that case). */
    summary: z.string().max(800),
});
/** Payload for the `brand_evolution` feed card. One card per tick per
 *  brand, even when multiple diffs propose. Founder approves the whole
 *  bundle (Phase-1) — granular per-diff approve/reject is v2. */
export const brandEvolutionCardPayloadSchema = z.object({
    kind: z.literal('brand_evolution'),
    /** Brain version this diff bundle was computed against. The applier
     *  refuses to apply if the current brain has moved past this version
     *  (someone else edited the brain in the meantime — re-run the coach). */
    brainVersionBefore: z.number().int().min(1),
    summary: z.string().min(1).max(800),
    diffs: z.array(brainDiffSchema).min(1).max(8),
});
/** Informational card emitted when Mag auto-applies a diff bundle
 *  (s211). Lands status='acked' on creation — no founder action
 *  needed. Surfaces what Mag did so the founder isn't surprised by
 *  brain changes she didn't see. Authority for the decision_type was
 *  A0 at the time of apply; the card itself does NOT change authority. */
export const brandEvolutionAutoAppliedPayloadSchema = z.object({
    kind: z.literal('brand_evolution_auto_applied'),
    brainVersionBefore: z.number().int().min(1),
    brainVersionAfter: z.number().int().min(1),
    /** One-line Mag-voice summary of what changed. */
    summary: z.string().min(1).max(400),
    /** The diffs that actually landed. Same shape as brand_evolution
     *  card, but already applied — render is read-only. */
    diffs: z.array(brainDiffSchema).min(1).max(8),
});
//# sourceMappingURL=brand-coach.js.map